import { useState } from 'react';
import { isApiError } from '@aiptyg/types';
import type { MessageImportBatchDTO, MessageImportRowDTO } from '../types/tasks.types';
import { commitMessageImport, exportMessageUnsentItems, getMessageImportBatch, previewMessageImport } from '../services/tasks.service';

interface MessageImportPanelProps {
  taskId?: number;
  mode?: 'edit' | 'create';
  initialBatch?: MessageImportBatchDTO | null;
  pendingFile?: File | null;
  onPendingFileChange?: (file: File | null) => void;
  idempotencyKey?: string;
  onIdempotencyKeyChange?: (value: string) => void;
  onCommitted?: (batch: MessageImportBatchDTO) => void | Promise<void>;
}

const STATUS_LABELS: Record<string, string> = {
  preview_ready: '预览可提交',
  committed: '已提交',
  failed: '失败',
  expired: '已过期',
  valid: '可导入',
  invalid_format: '格式错误',
  duplicate_in_batch: '批内重复',
  duplicate_existing: '已存在',
  suppressed: '已抑制',
  blocked_content: '内容阻断',
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    return e.message;
  }
  if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
    return e.message;
  }
  return '操作失败';
}

function batchSummary(batch: MessageImportBatchDTO) {
  return [
    ['总行数', batch.total_rows],
    ['可导入', batch.valid_rows],
    ['错误行', batch.invalid_rows],
    ['重复行', batch.duplicate_rows],
    ['抑制行', batch.suppressed_rows],
    ['已提交', batch.committed_rows],
  ] as const;
}

const MESSAGE_IMPORT_TEMPLATE = [
  'recipient_type,recipient,variable,body',
  'phone,+8613800000000,尊敬的用户：,请确认本次服务通知。',
  'email,user@example.com,Hi ,please confirm this service notice.',
].join('\n');

function csvCell(value: string): string {
  if (!/[",\r\n]/.test(value)) {
    return value;
  }
  return `"${value.replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([`\uFEFF${csv}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadMessageImportTemplate() {
  downloadCsv('message-import-template.csv', MESSAGE_IMPORT_TEMPLATE.split('\n').map((row) => row.split(',')));
}

function invalidImportRows(batch: MessageImportBatchDTO | null): MessageImportRowDTO[] {
  return batch?.rows?.filter((row) => row.status !== 'valid') ?? [];
}

function downloadInvalidMessageImportRows(batch: MessageImportBatchDTO) {
  const rows = invalidImportRows(batch);
  if (rows.length === 0) return;
  downloadCsv(
    `message-import-invalid-${batch.batch_id}.csv`,
    [
      ['recipient_type', 'recipient', 'variable', 'body'],
      ...rows.map((row) => [
        row.raw_recipient_type ?? '',
        row.raw_recipient ?? '',
        row.raw_variable ?? '',
        row.raw_body ?? '',
      ]),
    ],
  );
}

function ErrorRows({ rows }: { rows: MessageImportRowDTO[] }) {
  const errorRows = rows.filter((row) => row.status !== 'valid');
  if (errorRows.length === 0) {
    return <p className="text-sm text-gray-400">没有服务端返回的错误行。</p>;
  }
  return (
    <div className="max-h-72 overflow-auto rounded-md border border-gray-200">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
            <th className="px-3 py-2 font-medium">行号</th>
            <th className="px-3 py-2 font-medium">状态</th>
            <th className="px-3 py-2 font-medium">错误码</th>
          </tr>
        </thead>
        <tbody>
          {errorRows.map((row) => (
            <tr key={`${row.row_no}-${row.status}-${row.error_code ?? ''}`} className="border-b border-gray-100 last:border-b-0">
              <td className="px-3 py-2 font-mono text-xs tabular-nums text-gray-600">{row.row_no}</td>
              <td className="px-3 py-2 text-gray-900">{STATUS_LABELS[row.status] ?? row.status}</td>
              <td className="px-3 py-2 font-mono text-xs text-gray-500">{row.error_code || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MessageImportPanel({
  taskId,
  mode = 'edit',
  initialBatch,
  pendingFile,
  onPendingFileChange,
  idempotencyKey: controlledIdempotencyKey,
  onIdempotencyKeyChange,
  onCommitted,
}: MessageImportPanelProps) {
  const isCreateMode = mode === 'create';
  const [internalFile, setInternalFile] = useState<File | null>(null);
  const [internalIdempotencyKey, setInternalIdempotencyKey] = useState<string>(() => crypto.randomUUID());
  const [batchId, setBatchId] = useState<string>(() => initialBatch?.batch_id ? String(initialBatch.batch_id) : '');
  const [batch, setBatch] = useState<MessageImportBatchDTO | null>(() => initialBatch ?? null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const file = isCreateMode ? pendingFile ?? null : internalFile;
  const idempotencyKey = controlledIdempotencyKey ?? internalIdempotencyKey;
  const setSelectedFile = (next: File | null) => {
    if (isCreateMode) {
      onPendingFileChange?.(next);
      onIdempotencyKeyChange?.(crypto.randomUUID());
      return;
    }
    setInternalFile(next);
  };
  const setEffectiveIdempotencyKey = (value: string) => {
    if (onIdempotencyKeyChange) {
      onIdempotencyKeyChange(value);
      return;
    }
    setInternalIdempotencyKey(value);
  };
  const regenerateIdempotencyKey = () => setEffectiveIdempotencyKey(crypto.randomUUID());

  const handlePreview = async () => {
    if (isCreateMode || !taskId || !file || !idempotencyKey.trim()) return;
    setLoading(true);
    setError('');
    try {
      const next = await previewMessageImport(taskId, file, idempotencyKey.trim());
      setBatch(next);
      setBatchId(String(next.batch_id));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!taskId) return;
    const targetBatchID = String(batch?.batch_id || batchId).trim();
    if (!targetBatchID) return;
    setLoading(true);
    setError('');
    try {
      const next = await commitMessageImport(taskId, targetBatchID);
      setBatch(next);
      await onCommitted?.(next);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!taskId || !batchId.trim()) return;
    setLoading(true);
    setError('');
    try {
      setBatch(await getMessageImportBatch(taskId, batchId.trim()));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleExportUnsent = async () => {
    if (!taskId) return;
    setExporting(true);
    setError('');
    try {
      await exportMessageUnsentItems(taskId);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setExporting(false);
    }
  };

  const canCommit = Boolean(batch?.batch_id) && batch?.status === 'preview_ready' && (batch?.valid_rows ?? 0) > 0;
  const errorRows = invalidImportRows(batch);
  const hasImportIssues = errorRows.length > 0;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-medium text-gray-900">收件人池导入</h2>
          <p className="mt-1 text-xs text-gray-500">
            iMessage 支持手机号码和邮箱，同一 CSV/XLSX 可混合导入。recipient_type 填 phone 或 email，recipient 填对应地址；新模板正文按 variable + body 生成。导入结果不代表收件人已开通 iMessage。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadMessageImportTemplate}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
          >
            下载模板
          </button>
          {!isCreateMode && (
            <button
              onClick={handleExportUnsent}
              disabled={exporting}
              className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              {exporting ? '导出中...' : '导出未发送成功收件人'}
            </button>
          )}
          <button
            onClick={regenerateIdempotencyKey}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
          >
            生成幂等键
          </button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
        <label className="flex h-10 cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50">
          <span className="truncate">{file ? file.name : '选择 CSV/XLSX 文件'}</span>
          <input
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(event) => setSelectedFile(event.target.files?.[0] ?? null)}
          />
        </label>
        <button
          onClick={handlePreview}
          disabled={isCreateMode || !file || !idempotencyKey.trim() || loading}
          className="h-10 rounded-md bg-primary px-4 text-sm text-white hover:bg-primary/90 disabled:opacity-40"
        >
          {isCreateMode ? '创建后导入' : loading ? '处理中...' : 'Preview'}
        </button>
      </div>

      <div className={isCreateMode ? 'grid gap-3' : 'grid gap-3 md:grid-cols-[minmax(0,1fr)_160px_160px]'}>
        <input
          value={idempotencyKey}
          onChange={(e) => setEffectiveIdempotencyKey(e.target.value)}
          className="h-9 rounded-md border border-gray-300 px-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary/50"
          placeholder="Idempotency-Key"
        />
        {!isCreateMode && (
          <>
            <input
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              className="h-9 rounded-md border border-gray-300 px-3 font-mono text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder="批次 ID"
            />
            <button
              onClick={handleRestore}
              disabled={!batchId.trim() || loading}
              className="h-9 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              恢复批次
            </button>
          </>
        )}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {batch && (
        <div className="space-y-4 rounded-md border border-gray-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700">
              批次 <span className="font-mono tabular-nums">{batch.batch_id}</span>
              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{STATUS_LABELS[batch.status] ?? batch.status}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {hasImportIssues && (
                <button
                  onClick={() => downloadInvalidMessageImportRows(batch)}
                  className="h-9 rounded-md border border-red-200 px-4 text-sm text-red-600 hover:bg-red-50"
                >
                  下载待修正表格
                </button>
              )}
              <button
                onClick={handleCommit}
                disabled={!canCommit || loading}
                className="h-9 rounded-md bg-gray-900 px-4 text-sm text-white hover:bg-gray-800 disabled:opacity-40"
              >
                提交合规数据
              </button>
            </div>
          </div>

          {hasImportIssues && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              存在不合规数据：{errorRows.length} 行暂不能导入。可先提交合规数据，再下载待修正表格，运营修改后重新导入。
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-3">
            {batchSummary(batch).map(([label, value]) => (
              <div key={label} className="rounded-md bg-gray-50 px-3 py-2">
                <div className="text-xs text-gray-500">{label}</div>
                <div className="mt-1 font-mono text-lg tabular-nums text-gray-900">{value}</div>
              </div>
            ))}
          </div>

          <ErrorRows rows={batch.rows ?? []} />
        </div>
      )}
    </section>
  );
}
